import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { getEligibleUsers } from '../api/chat';
import { fetchEmployeeIdentities } from '../hrms/api/employeeIdentities';
import { useAuth } from './AuthContext';
import {
  buildEmployeeIdentityIndex, buildPhotoDirectory, type DirectoryPerson, type EmployeeIdentity,
} from '../utils/people/peopleDirectory';
import { PeopleDirectoryContext, photoLookup, type PeopleDirectory } from './PeopleDirectoryContext';

// Stable empty references so a signed-out mount doesn't churn the memoised value.
const NO_COLLEAGUES: DirectoryPerson[] = [];
const NO_EMPLOYEES: EmployeeIdentity[] = [];

export function PeopleDirectoryProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const selfId = user?.id ?? null;
  const selfPhoto = user?.avatar_url ?? null;

  const [colleagues, setColleagues] = useState<DirectoryPerson[]>(NO_COLLEAGUES);
  const [employees, setEmployees] = useState<EmployeeIdentity[]>(NO_EMPLOYEES);
  const [employeesWanted, setEmployeesWanted] = useState(false);

  // Which signed-in user each fetch was for. Checking this ref on resolve (not
  // an `alive` cleanup flag) survives React 18 StrictMode's mount/cleanup/mount
  // replay — so the response is applied, not dropped — and guarantees a late
  // response from a previous user is never applied under the next one.
  const colleaguesFor = useRef<string | null>(null);
  const employeesFor = useRef<string | null>(null);

  useEffect(() => {
    if (!selfId) {
      colleaguesFor.current = null;
      employeesFor.current = null;
      setColleagues(NO_COLLEAGUES);
      setEmployees(NO_EMPLOYEES);
      setEmployeesWanted(false);
      return;
    }
    if (colleaguesFor.current === selfId) return;
    // A newly signed-in user: forget the previous directory and re-arm the bridge.
    colleaguesFor.current = selfId;
    employeesFor.current = null;
    setColleagues(NO_COLLEAGUES);
    setEmployees(NO_EMPLOYEES);
    getEligibleUsers()
      .then((list) => {
        if (colleaguesFor.current === selfId) {
          setColleagues(Array.isArray(list) ? (list as DirectoryPerson[]) : NO_COLLEAGUES);
        }
      })
      .catch(() => { /* empty directory → rows fall back to initials */ });
  }, [selfId]);

  useEffect(() => {
    if (!selfId || !employeesWanted || employeesFor.current === selfId) return;
    employeesFor.current = selfId;
    fetchEmployeeIdentities()
      .then((list) => {
        if (employeesFor.current === selfId) {
          setEmployees(Array.isArray(list) ? list : NO_EMPLOYEES);
        }
      })
      .catch(() => { /* leave employees empty */ });
  }, [selfId, employeesWanted]);

  const requestEmployees = useCallback(() => setEmployeesWanted(true), []);
  const photos = useMemo(
    () => buildPhotoDirectory(colleagues, selfId ? { avatar_url: selfPhoto, id: selfId } : null),
    [colleagues, selfId, selfPhoto],
  );
  const employeeIndex = useMemo(() => buildEmployeeIdentityIndex(employees), [employees]);
  const value = useMemo<PeopleDirectory>(
    () => ({ employees: employeeIndex, photoForUser: photoLookup(photos), requestEmployees }),
    [employeeIndex, photos, requestEmployees],
  );

  return <PeopleDirectoryContext.Provider value={value}>{children}</PeopleDirectoryContext.Provider>;
}
